import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  promotionDeadline,
  promotionHoursLeft,
} from "../../constants/subscription";

/**
 * The promotion's name under a discounted price, and the terms it runs on.
 *
 * The name alone ("Lansarea Oficială") says there is an offer but not how long
 * it lasts or what happens after, which is exactly what a buyer is entitled to
 * know before paying. The deadline is the promotion's own `EndsAt` from the
 * server, never a date typed into the copy: a SystemOwner extends a promotion
 * from the pricing tab, and the page has to follow.
 *
 * A toggletip, not a title attribute: it has to open on a tap, because most
 * visitors are on a phone, and it holds a link to the terms, which a tooltip
 * may not. Hover and focus preview it; a click pins it open until a click
 * elsewhere or Escape.
 *
 * The countdown stays on the visible line, because under 48 hours it is the
 * thing worth saying first.
 */
const PromotionNote = ({ name, endsAt, planValue }) => {
  const { t, i18n } = useTranslation();
  const bubbleId = useId();
  const wrapperRef = useRef(null);
  const [peek, setPeek] = useState(false);
  const [pinned, setPinned] = useState(false);

  // Close a pinned note when the visitor taps or clicks anywhere else.
  useEffect(() => {
    if (!pinned) return undefined;
    const onPointerDown = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setPinned(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [pinned]);

  const locale = i18n?.language?.startsWith("en") ? "en-GB" : "ro-RO";
  const deadline = promotionDeadline(endsAt, locale);
  const hours = promotionHoursLeft(endsAt);
  const open = peek || pinned;

  return (
    <div className="relative mt-1.5 flex items-center gap-1 text-xs font-semibold text-brand-700">
      <span>
        {name}
        {hours ? ` · ${t("Pricing.discount.endsInHours", { count: hours })}` : ""}
      </span>

      {deadline && (
        <span
          ref={wrapperRef}
          className="inline-flex"
          onMouseEnter={() => setPeek(true)}
          onMouseLeave={() => setPeek(false)}
          onFocus={() => setPeek(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setPeek(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setPeek(false);
              setPinned(false);
            }
          }}
        >
          <button
            type="button"
            aria-expanded={open}
            aria-controls={open ? bubbleId : undefined}
            aria-label={t("Pricing.promotion.detailsLabel")}
            data-testid={`promotion-info-${planValue}`}
            onClick={() => setPinned((p) => !p)}
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-brand-600 transition hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600"
          >
            <i className="bi bi-info-circle" aria-hidden="true" />
          </button>

          {/* Anchored to the whole line rather than to the icon, so the bubble
              takes the card's width and can never run off the side of a phone
              screen. The gap above it is padding, not margin: the pointer
              crosses it on the way to the link without leaving the note. */}
          {open && (
            <span
              id={bubbleId}
              role="note"
              data-testid={`promotion-terms-${planValue}`}
              className="absolute inset-x-0 top-full z-20 block pt-2"
            >
              <span className="block rounded-xl bg-ink-900 p-3.5 text-left text-xs leading-relaxed font-normal text-white shadow-lg">
                <span className="block font-semibold">
                  {t("Pricing.promotion.validUntil", deadline)}
                </span>
                <span className="mt-1 block text-white/80">
                  {t("Pricing.promotion.afterwards")}{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-white underline underline-offset-2 hover:text-brand-100"
                  >
                    {t("Pricing.promotion.termsLink")}
                  </a>
                  .
                </span>
              </span>
            </span>
          )}
        </span>
      )}
    </div>
  );
};

PromotionNote.propTypes = {
  name: PropTypes.string,
  endsAt: PropTypes.string,
  planValue: PropTypes.number,
};

export default PromotionNote;
